import { apiRoutes } from '@socialfy/api-contract';
import type { QueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { de } from '@/i18n/de';
import { format } from '@/i18n/format';
import { problemText } from '@/i18n/messages';
import { useToast } from '@/shell/ui/toast';
import { type ApiClient, createApiClient } from './client';
import { ApiProblem } from './problem';
import { ApiProvider } from './provider';
import { createQueryClient, useApiQuery } from './query';
import {
  LOGIN_PATH,
  loginUrl,
  SessionContext,
  type SessionMe,
  type SessionMode,
  type SessionStatus,
  type SessionValue,
} from './session';

/** Schlüssel der Abfrage `core.getMe` (siehe `useApiQuery`: `[operationId, Eingabe]`). */
const ME_KEY = ['core.getMe', null] as const;

type Location = { pathname: string; search: string };

/** Zustand des Moduswechsels: laufender Wechsel und Generation (siehe `setMode`). */
type ModeSwitch = { generation: number; running: Promise<void> | null };

type SessionProviderProps = {
  children: ReactNode;
  /** Lädt den Benutzer beim Start; auf der Seite „Kein Zugang“ ausgeschaltet. */
  loadMe?: boolean;
  /** Weiterleitung zu einer anderen Adresse (Standard: `window.location.assign`). */
  assign?: (to: string) => void;
  /** Aktuelle Adresse für das Rücksprungziel (Standard: `window.location`). */
  getLocation?: () => Location;
  /** Wird aufgerufen, wenn das Konto angemeldet, aber nicht freigeschaltet ist. */
  onNoAccess?: () => void;
  /** Wartezeit vor Wiederholungen in ms (Tests: 0). */
  retryDelay?: number;
  fetch?: typeof fetch;
};

function statusOf(loadMe: boolean, me: SessionMe | undefined, error: Error | null): SessionStatus {
  if (me !== undefined) {
    return 'ready';
  }
  if (!loadMe) {
    return 'idle';
  }
  if (error === null) {
    return 'loading';
  }
  return error instanceof ApiProblem && error.status === 403 && !error.isCsrf
    ? 'no-access'
    : 'error';
}

function SessionState({
  client,
  queryClient,
  loadMe,
  assign,
  tokenRef,
  modeSwitch,
  onNoAccess,
  children,
}: {
  client: ApiClient;
  queryClient: QueryClient;
  loadMe: boolean;
  assign: (to: string) => void;
  tokenRef: { current: string | null };
  modeSwitch: { current: ModeSwitch };
  onNoAccess: () => void;
  children: ReactNode;
}) {
  const meQuery = useApiQuery(apiRoutes.core.getMe, undefined, { enabled: loadMe });
  const me = meQuery.data;
  const status = statusOf(loadMe, me, meQuery.error);
  const notified = useRef(false);

  useEffect(() => {
    if (me !== undefined) {
      tokenRef.current = me.csrfToken;
    }
  }, [me, tokenRef]);

  useEffect(() => {
    if (status === 'no-access' && !notified.current) {
      notified.current = true;
      onNoAccess();
    }
  }, [status, onNoAccess]);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      me,
      error: meQuery.error,
      setMode(mode: SessionMode) {
        // Ein Wechsel zur Zeit; ein zweiter Aufruf teilt sich den laufenden.
        if (modeSwitch.current.running !== null) {
          return modeSwitch.current.running;
        }
        // Die Generation zählt bei Beginn und Ende hoch: jede Sitzungsantwort, die währenddessen (oder davor)
        // gestartet wurde, gehört zum alten Modus und wird verworfen (siehe `onCsrfRejected`).
        modeSwitch.current.generation += 1;
        const run = (async () => {
          try {
            // Laufende Abrufe (auch `me`) gehören zum alten Modus: abbrechen, damit sie das Ergebnis nicht überschreiben
            await queryClient.cancelQueries();
            const next = await client.call(apiRoutes.core.setSessionMode, { body: { mode } });
            await queryClient.cancelQueries();
            // Ab hier gilt der neue Modus: alles, was vorher gestartet wurde, ist veraltet (auch wenn es noch eintrifft)
            modeSwitch.current.generation += 1;
            tokenRef.current = next.csrfToken;
            queryClient.setQueryData([...ME_KEY], next);
            // Alle anderen Daten gehören zur anderen Datenbank: verwerfen und aktive Ansichten neu laden.
            await queryClient.resetQueries({
              predicate: (query) => query.queryKey[0] !== ME_KEY[0],
            });
          } finally {
            modeSwitch.current.generation += 1;
            modeSwitch.current.running = null;
          }
        })();
        modeSwitch.current.running = run;
        return run;
      },
      async refresh() {
        await meQuery.refetch();
      },
      async logout() {
        await client.call(apiRoutes.core.logout);
        assign(LOGIN_PATH);
      },
    }),
    [status, me, meQuery.error, meQuery, client, queryClient, tokenRef, modeSwitch, assign],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Sitzung der Anwendung: legt Client und Zwischenspeicher an (Schutzmarke nur im Arbeitsspeicher), lädt den
 * Benutzer (`GET /me`), leitet bei 401 zur Anmeldung, lädt bei abgelehnter Schutzmarke die Sitzung neu und
 * wiederholt einmal, wechselt den Modus und meldet ab. Darüber muss ein `ToastProvider` sitzen (Hinweise
 * „Im Demo-Modus nicht möglich.“ und Grenzen).
 */
export function SessionProvider({
  children,
  loadMe = true,
  assign = (to) => window.location.assign(to),
  getLocation = () => window.location,
  onNoAccess = () => undefined,
  retryDelay,
  fetch: fetchImpl,
}: SessionProviderProps) {
  const { toast } = useToast();
  const tokenRef = useRef<string | null>(null);
  const modeSwitch = useRef<ModeSwitch>({ generation: 0, running: null });
  const latest = useRef({ assign, getLocation, toast });
  latest.current = { assign, getLocation, toast };

  const [{ client, queryClient }] = useState(() => {
    const queryClient = createQueryClient({
      ...(retryDelay === undefined ? {} : { retryDelay }),
      onProblem: (problem) => {
        if (problem.code === 'demo_read_only') {
          latest.current.toast({ message: de.demo.notPossible });
        } else if (problem.isLimit) {
          const retryAt =
            problem.retryAfterSeconds === null
              ? undefined
              : format.time(new Date(Date.now() + problem.retryAfterSeconds * 1000));
          latest.current.toast({
            message: problemText(problem.code, { reason: problem.reason, retryAt }),
          });
        }
      },
    });
    const client: ApiClient = createApiClient({
      ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
      getCsrfToken: () => tokenRef.current,
      onUnauthorized: () => latest.current.assign(loginUrl(latest.current.getLocation())),
      onCsrfRejected: async () => {
        const started = modeSwitch.current.generation;
        const fresh = await client.call(apiRoutes.core.getMe);
        if (modeSwitch.current.generation !== started) {
          // Währenddessen wurde der Modus gewechselt: die Antwort ist veraltet; der Wechsel hat Marke und Benutzer gesetzt
          await modeSwitch.current.running?.catch(() => undefined);
          return;
        }
        tokenRef.current = fresh.csrfToken;
        queryClient.setQueryData([...ME_KEY], fresh);
      },
    });
    return { client, queryClient };
  });

  return (
    <ApiProvider client={client} queryClient={queryClient}>
      <SessionState
        client={client}
        queryClient={queryClient}
        loadMe={loadMe}
        assign={assign}
        tokenRef={tokenRef}
        modeSwitch={modeSwitch}
        onNoAccess={onNoAccess}
      >
        {children}
      </SessionState>
    </ApiProvider>
  );
}
